import {
  AMOUNT,
  CAPITAL,
  MAX_SHARPE,
  RESAMPLING_FREQUENCY_OPTIONS,
  RESERVE_TICKER,
  SUCCESS,
  WEIGHT
} from "../utils/constants";
import {
  checkWrongMinMax,
  checkWrongWeightSum,
  composeGlobalLimits,
  composeRemovedTickers,
  composeTickerRuleList,
  getBaseURL,
  getObjectiveValue,
  mapPortfolioToArray,
  mapTickerIcons,
  multiply100,
  prepareForConversion,
  reverseMapPortfolio
} from "../utils/helper";
import VueScrollTo from "vue-scrollto";
import axios from "axios";
import fileDownload from "js-file-download";
import moment from "moment";
import { v4 as uuidv4 } from "uuid";

const url = getBaseURL(false, true);

export default {
  namespaced: true,
  state: {
    isOptimizingPortfolio: false,
    rules: [],
    dataSince: moment("20.3.2019", "DD.MM.YYYY").toDate(),
    portfolio: null,
    duplicatesPrevented: 0,
    wrongMinMaxPrevented: 0,
    wrongWeightSumPrevented: 0,
    pointDetail: null,
    removedTickers: [],
    specialPortfolioId: "",
    recalculatedPortfolio: null,
    expandedChart: {
      type: "",
      data: []
    },
    replacePortfolioCoords: null,
    savedPortfolios: [],
    portfoliosLoad: false,
    notCalculatedPortfolios: [],
    newImportedPortfolios: [],
    cash: null,
    useFractions: false,
    isWholeStocks: false,
    resamplingFrequency: RESAMPLING_FREQUENCY_OPTIONS()[0],
    poolSP100: [],
    pools: [],
    poolToEdit: null,
    selectedPool: null,
    saveModalItem: null,
    portfolioHistories: null,
    selectedHistoryVersion: null,
    isLoadingHistoryVersion: false,
    editMode: false,
    tickersNonExistList: [],
    missingTickerFinished: false,
    existTickers: false,
    defaultPortfolio: null,
    isCalculatingFrontier: false,
    isCalculatingSuggestion: false,
    suggestedOptimalization: null,
    isCalculatingMeasures: false,
    availableTickers: null,
    tickersWithMissingData: []
  },
  mutations: {
    setAvailableTickers: (state, data) => {
      state.availableTickers = data;
    },
    setReplacePortfolioCoords: (state, data) => {
      state.replacePortfolioCoords = data;
    },
    setIsLoadingHistoryVersion: (state, data) => {
      state.isLoadingHistoryVersion = data;
    },
    setSelectedHistoryVersion: (state, data) => {
      state.selectedHistoryVersion = data;
    },
    setDefaultPortfolio: (state, data) => {
      state.defaultPortfolio = data;
    },
    setIsCalculatingFrontier: (state, data) => {
      state.isCalculatingFrontier = data;
    },
    setIsCalculatingSuggestion: (state, data) => {
      state.isCalculatingSuggestion = data;
    },
    addRule: (state, data) => {
      if (data.rule.value === "tickers") {
        state.rules = [...state.rules, { ...data, id: uuidv4() }];
      } else {
        const hasSameRule = state.rules.find(
          rule =>
            rule.rule.value === data.rule.value &&
            rule.limit.value === data.limit.value
        );
        const oppositeLimit = state.rules.find(
          rule => rule.rule.value === data.rule.value
        );
        const isWrongMinMax = checkWrongMinMax(data, oppositeLimit);
        const wrongWeightSum = checkWrongWeightSum(data, state.rules);

        if (hasSameRule) {
          state.duplicatesPrevented = state.duplicatesPrevented + 1;
        } else if (isWrongMinMax) {
          state.wrongMinMaxPrevented = state.wrongMinMaxPrevented + 1;
        } else if (wrongWeightSum) {
          state.wrongWeightSumPrevented = state.wrongWeightSumPrevented + 1;
        } else {
          state.rules = [...state.rules, { ...data, id: uuidv4() }];
        }
      }
    },
    removeRule: (state, data) => {
      state.rules = state.rules.filter(rule => rule.id !== data);
    },
    clearRules: state => {
      state.rules = [];
    },
    setCash: (state, data) => {
      state.cash = data && !isNaN(data) ? data : null;
    },
    setDataSince: (state, data) => {
      state.dataSince = data;
    },
    setIsWholeStocks: (state, data) => {
      state.isWholeStocks = data;
    },
    setIsOptimizingPortfolio: (state, data) =>
      (state.isOptimizingPortfolio = data),
    setPointDetail: (state, data) => {
      state.pointDetail = data;
      state.specialPortfolioId = "";
    },
    setUseFractions: (state, data) => {
      state.useFractions = data;
    },
    clearPointDetail: state => {
      state.pointDetail = null;
      state.specialPortfolioId = "";
    },
    setSuggestedOptimalization: (state, data) => {
      state.suggestedOptimalization = data;
    },
    removeTicker: (state, data) => {
      state.removedTickers = [...state.removedTickers, data];
    },
    restoreTicker: (state, data) => {
      state.removedTickers = state.removedTickers.filter(
        ticker => ticker !== data
      );
    },
    clearRemovedTickers: state => {
      state.removedTickers = [];
    },
    setPortfolio: (state, data) => {
      const newPortfolio = data;
      if (data && data.portfolio_input) {
        newPortfolio.portfolio_input.custom_portfolio =
          data.portfolio_input.custom_portfolio;
        state.cash = data.portfolio_input.start_cash
          ? data.portfolio_input.start_cash.toFixed(2)
          : null;
      }
      state.portfolio = data;
    },
    setRecalculatedPortfolio: (state, data) => {
      state.recalculatedPortfolio = data;
      if (data !== null) {
        state.recalculatedPortfolio = {
          ...state.recalculatedPortfolio,
          custom_portfolio_data: state.removedTickers.length !== 0,
          removedTickers: state.removedTickers
        };
      }
    },
    replacePortfolio: (state, data) => {
      state.recalculatedPortfolio = null;
      state.replacePortfolioCoords = data.coords;
    },
    setCustomPortfolio: (state, {pool_id, ...arr}) => {
      const data = Object.values(arr);
      if (state.portfolio === null) {
        const obj = {
          portfolio_input: {
            custom_portfolio: data,
            tickers: Object.keys(data),
            ...pool_id && {global_limits: {
              pool_id
            }}
          }
        };
        state.portfolio = obj;
      }
      if (data[0].capital !== 0) {
        const cash = data.reduce((acc, curr) => acc + curr.capital, 0);
        state.cash = cash && cash !== state.cash ? cash.toFixed(2) : state.cash;
      }
      state.portfolio.portfolio_input.custom_portfolio = data;
    },
    setSpecialPortfolio: (state, data) => {
      state.specialPortfolioId = data.portfolioId;
      state.pointDetail = data.pointDetail;
    },
    setExpandedChart: (state, data) => {
      state.expandedChart = data;
    },
    setSavedPortfolios: (state, data) => {
      state.savedPortfolios = data;
    },
    setPortfoliosLoad: (state, data) => {
      state.portfoliosLoad = data;
    },
    setNotCalculatedPortfolios: (state, data) => {
      state.notCalculatedPortfolios = data;
    },
    analyzedPortfolio: (state, data) => {
      state.notCalculatedPortfolios = state.notCalculatedPortfolios.filter(
        item => item.portfolio_id.toString() !== data.toString()
      );
    },
    setNewImportedPortfolios: (state, data) => {
      state.newImportedPortfolios = state.newImportedPortfolios.concat(data);
    },
    openImportedPortfolio: (state, data) => {
      state.newImportedPortfolios = state.newImportedPortfolios.filter(
        id => id !== data
      );
    },
    setResamplingFrequency: (state, data) => {
      state.resamplingFrequency = data;
    },
    setPoolSP100: (state, data) => {
      state.poolSP100 = data;
    },
    setPools: (state, data) => {
      state.pools = data;
    },
    setPoolToEdit: (state, data) => {
      state.poolToEdit = data;
    },
    setSelectedPool: (state, data) => {
      state.selectedPool = data;
    },
    setSaveModalItem: (state, data) => {
      state.saveModalItem = data;
    },
    setMeasurements: (state, data) => {
      state.portfolio = {
        ...state.portfolio,
        portfolio_output: state.portfolio.portfolio_output
          ? {
              ...state.portfolio.portfolio_output,
              custom_portfolio: data
            }
          : {
              custom_portfolio: data
            }
      };
    },
    setEachStockResults: (state, data) => {
      state.portfolio = {
        ...state.portfolio,
        portfolio_output: state.portfolio.portfolio_output
          ? {
              ...state.portfolio.portfolio_output,
              each_stock_results: data
            }
          : {
              each_stock_results: data
            }
      };
    },
    setPortfolioHistories: (state, data) => {
      state.portfolioHistories = data;
    },
    setPortfolioId: (state, data) => {
      state.portfolio = {
        ...state.portfolio,
        portfolio_id: data
      };
    },
    setEditMode: (state, data) => {
      state.editMode = data;
    },
    setTickersNonExistList: (state, data) => {
      state.tickersNonExistList = data;
    },
    setMissingTickerFinished: (state, data) => {
      state.missingTickerFinished = data;
    },
    setExistTickers: (state, data) => {
      state.existTickers = data;
    },
    setCustomPortfolioOutput: (state, data) => {
      state.portfolio = {
        ...state.portfolio,
        portfolio_output: state.portfolio.portfolio_output
          ? {
              ...state.portfolio.portfolio_output,
              custom_portfolio: data
            }
          : {
              custom_portfolio: data
            }
      };
    },
    setIsCalculatingMeasures: (state, data) => {
      state.isCalculatingMeasures = data;
    },
    setTickersWithMissingData: (state, data) => {
      state.tickersWithMissingData = data;
    }
  },
  actions: {
    getAvailableTickers({ commit }) {
      return axios.get(`${url}/available_tickers/`).then(res => {
        commit("setAvailableTickers", res.data);
      });
    },
    generatePDFResult(store, data) {
      return axios.get(`${url}/portfolio/history/${data}/pdf`, {
        responseType: "arraybuffer"
      });
    },
    getHistoryVersion({ commit }, id) {
      commit("setIsLoadingHistoryVersion", true);
      return axios
        .get(`${url}/portfolio/history/${id}`)
        .then(res => commit("setSelectedHistoryVersion", res.data))
        .finally(() => commit("setIsLoadingHistoryVersion", false));
    },
    rebalancePortfolio({ state }) {
      return axios.put(
        `${url}/portfolio/history/${state.portfolio.history_id}`,
        {
          custom_portfolio_input: reverseMapPortfolio(
            state.portfolio.portfolio_input.custom_portfolio
          ),
          custom_portfolio_output:
            state.portfolio.portfolio_output.custom_portfolio
        }
      );
    },
    getPortfolioHistories({ commit }, portfolioId) {
      return axios.get(`${url}/portfolio/${portfolioId}/histories`).then(res =>
        commit(
          "setPortfolioHistories",
          res.data.filter(item => item.custom_portfolio_output.length !== 0)
        )
      );
    },
    getDefaultEfficientFrontier({ commit }, pool) {
      const poolId = pool && pool.pool_id;
      let endpoint = "";
      switch (poolId) {
        case -3:
          endpoint = "sp500_portfolio"
          break;
        default:
          endpoint = "default_portfolio"
          break;
      }
      if (endpoint) {
        return axios
          .get(`${url}/${endpoint}`)
          .then(res => commit("setDefaultPortfolio", res.data));
      }
    },
    clearPortfolio({ commit, state }, data) {
      //null the portfolio - screen with imports
      if (data) {
        commit("setPortfolio", null);
      }
      commit("setRecalculatedPortfolio", null);
      commit("clearRemovedTickers");
      commit("clearPointDetail");
      commit("clearRules");
      commit("setIsWholeStocks", false);
      commit("setCash", null);
      commit("setSelectedHistoryVersion", null);
      commit("setSuggestedOptimalization", null);
      const defaultPool = state.pools.find(pool => pool.pool_id === -1);
      commit("setSelectedPool", defaultPool);
    },
    getSavedPortfolios({ commit }) {
      commit("setPortfoliosLoad", true);
      return axios.get(`${url}/portfolios/`).then(res => {
        commit("setSavedPortfolios", res.data);
        commit(
          "setNotCalculatedPortfolios",
          res.data.filter(
            item => item.last_history_obj.custom_portfolio_output.length === 0
          )
        );
        commit("setPortfoliosLoad", false);
      });
    },
    getSP100Pool(state) {
      return axios.get(`${url}/poolSP100/`).then(res => {
        state.commit("setPoolSP100", mapTickerIcons(res.data));
      });
    },
    savePortfolio({ dispatch, commit }, data) {
      return axios.put(`${url}/portfolioSave/`, data).then(res => {
        dispatch("getSavedPortfolios");
        if (data.history_id) {
          commit("setPortfolioId", res.data.portfolio_id);
        }
      });
    },
    exportPortfolio(state, data) {
      return axios
        .post(
          `${url}/portfolio/JsonToCsv`,
          data.custom_portfolio[0].weight
            ? data.custom_portfolio.map(item => ({
                ...item,
                weight: Number((item.weight / 100).toFixed(3))
              }))
            : data.custom_portfolio
        )
        .then(res =>
          fileDownload(
            res.data,
            `PortfolioExport ${moment().format("DD. MM. YYYY")}.csv`
          )
        );
    },
    downloadPortfolioExcel(state, data) {
      const formData = data.portfolios.length ? new FormData() : [];
      if (data.portfolios.length) {
        formData.append("portfolios_list", JSON.stringify(data.portfolios));
      }

      return axios
        .post(`${url}/portfolio/exportXLSX`, formData, {
          responseType: "blob"
        })
        .then(res => {
          const name =
            data.name && data.name !== ""
              ? `${data.name}.xlsx`
              : data.portfolios.length
              ? `PortfolioExport_${moment().format("DD.MM.YYYY")}.xlsx`
              : `All_portfolios_${moment().format("DD.MM.YYYY")}.xlsx`;
          fileDownload(res.data, name);
        });
    },
    downloadPortfolioExcelHistory({ dispatch }, data) {
      //data - array of objects with keys id and name
      data.forEach(item => {
        return axios.get(`${url}/portfolio/${item.id}/histories`).then(res => {
          const idArray = [];
          res.data.forEach(version => {
            if (version.custom_portfolio_output.length !== 0)
              idArray.push(version.history_id);
          });
          dispatch("downloadPortfolioExcel", {
            name: item.name,
            portfolios: idArray
          });
        });
      });
    },
    importPortfolios({ commit }, data) {
      const formData = new FormData();
      formData.append("start", data.start);
      formData.append("end", data.end);
      formData.append("file", data.file);
      return axios.post(`${url}/portfolio/importXLSX`, formData).then(res => {
        commit("setNewImportedPortfolios", res.data.portfolios);
      });
    },

    optimizePortfolio({ commit, dispatch, state }, data) {
      commit("setIsOptimizingPortfolio", true);
      commit("setIsCalculatingFrontier", true);

      const recalculatePortfolio =
        data.history_id !== null && !state.portfolio.done;

      const tickersRules = composeTickerRuleList(state.rules);
      const removedTickersComposition = composeRemovedTickers(
        state.portfolio,
        state.removedTickers,
        tickersRules
      );

      const global_limits = composeGlobalLimits(
        !recalculatePortfolio,
        state.rules,
        state.selectedPool.pool_id
      );

      const portfolio = reverseMapPortfolio(
        state.portfolio.portfolio_input.custom_portfolio
      );

      // recalculate - keeping id -> no result yet
      if (recalculatePortfolio) {
        const recalculateData = {
          start: data.start,
          end: data.end,
          custom_portfolio: portfolio,
          freq: state.resamplingFrequency.value,
          global_limits: global_limits,
          start_cash: state.cash ? state.cash : 0
        };

        return axios
          .post(
            `${url}/portfolioRecalculate/${data.history_id}`,
            recalculateData
          )
          .then(res => {
            commit("setMeasurements", res.data.custom_portfolio);
            commit("setEachStockResults", res.data.each_stock_results);
            dispatch("retrieveOptimizedPortfolio", {
              id: res.data.history_id,
              analyze: true
            });
          });
      } else {
        const formData = new FormData();
        formData.append("start", data.start);
        formData.append("end", data.end);
        formData.append("freq", state.resamplingFrequency.value);

        if (data.portfolio_id) {
          formData.append("portfolio_id", data.portfolio_id);
        }
        if (global_limits) {
          formData.append("global_limits", global_limits);
        }
        if (state.removedTickers.length || tickersRules.length) {
          formData.append("tickers_limit", removedTickersComposition);
        } else {
          formData.append("custom_portfolio", JSON.stringify(portfolio));
        }

        if (state.cash) {
          formData.append("start_cash", state.cash);
        }

        return axios.post(`${url}/portfolio/`, formData).then(res => {
          if (formData.custom_portfolio) {
            commit("setMeasurements", res.data.custom_portfolio);
            commit("setEachStockResults", res.data.each_stock_results);
          }
          return dispatch("retrieveOptimizedPortfolio", {
            id: res.data.history_id,
            analyze: true
          });
        });
      }
    },
    retrieveOptimizedPortfolio({ commit, state, dispatch }, data) {
      if (
        state.savedPortfolios.find(
          portfolio => portfolio.last_history_obj.history_id === data.id
        )
      ) {
        commit("setIsOptimizingPortfolio", true);
      }

      commit("setSelectedHistoryVersion", null);
      commit("setReplacePortfolioCoords", null);
      return axios.get(`${url}/portfolio/history/${data.id}`).then(res => {
        const tData = res.data;
        tData.portfolio_input.custom_portfolio = mapPortfolioToArray(
          res.data.portfolio_input.custom_portfolio
        );
        if (res.data.done && res.data.error === "") {
          if (state.portfolio && !state.portfolio.done) {
            VueScrollTo.scrollTo(".markowitz-chart", 1000, { offset: -100 });
          }
          commit(
            state.portfolio &&
              (state.removedTickers.length || state.rules.length)
              ? "setRecalculatedPortfolio"
              : "setPortfolio",
            tData
          );
          commit("setIsCalculatingFrontier", false);
          commit("setIsOptimizingPortfolio", false);
        }else if (!data.analyze) {
          commit("setPortfolio", tData);
          commit("setIsOptimizingPortfolio", false);
        } else if (res.data.done && res.data.error) { 
          throw {
            response: {
              data: {
                detail: res.data.error
              }
            }
          };
        }  else {
          if (
            !state.portfolio?.portfolio_output ||
            JSON.stringify(state.portfolio.portfolio_output) !==
              JSON.stringify(tData.portfolio_output)
          ) {
            commit("setIsOptimizingPortfolio", false);
          }
          setTimeout(() => dispatch("retrieveOptimizedPortfolio", data), 3000);
        }
      });
    },
    replaceStocksOptimalization({ commit, dispatch, state }, data) {
      const formData = new FormData();
      formData.append(
        "custom_portfolio",
        JSON.stringify(
          reverseMapPortfolio(state.portfolio.portfolio_input.custom_portfolio)
        )
      );
      formData.append("freq", state.resamplingFrequency.value);
      formData.append(
        "global_limits",
        composeGlobalLimits(true, state.rules, state.selectedPool.pool_id)
      );
      if (state.cash) {
        formData.append("start_cash", state.cash);
      }
      formData.append("objective", data.objective.value);
      formData.append("count_replace_assets", data.count_replace_assets);
      commit("setIsCalculatingSuggestion", true);

      return axios.post(`${url}/replaceStocks/`, formData).then(res => {
        dispatch("getCalculatedSuggestion", {
          id: res.data.task_id,
          objective: data.objective,
          replaced: data.count_replace_assets
        });
      });
    },

    getCalculatedSuggestion({ commit, dispatch, state }, data) {
      return axios.get(`${url}/replaceStocksResult/${data.id}`).then(res => {
        if (res.data.status === SUCCESS) {
          const values = [];
          for (const [key, value] of Object.entries(res.data.weights)) {
            const item = {};
            item["ticker"] = key;
            item["weight"] = value;
            values.push(item);
          }
          const percent = data.objective.value !== MAX_SHARPE;

          commit("setSuggestedOptimalization", {
            measures: res.data.values,
            values: values,
            name: data.objective.name + " (" + data.replaced + " ",
            changed: res.data.improve,
            new: percent
              ? multiply100(res.data.new_result).toString() + " %"
              : res.data.new_result,
            old: getObjectiveValue(
              data.objective.value,
              state.portfolio.portfolio_output.custom_portfolio
            )
          });
          commit("setIsCalculatingSuggestion", false);
        } else {
          setTimeout(() => dispatch("getCalculatedSuggestion", data), 8000);
        }
      });
    },
    getPools({ commit }) {
      return axios.get(`${url}/pools/`).then(res => {
        commit(
          "setPools",
          res.data
            .filter(pool => pool.pool_id !== -2)
            .map(pool => ({
              ...pool,
              tickers: pool.tickers ? mapTickerIcons(pool.tickers) : null
            }))
        );
        const defaultPool = res.data.find(pool => pool.pool_id === -1);
        commit("setSelectedPool", {
          ...defaultPool,
          tickers: mapTickerIcons(defaultPool.tickers)
        });
      });
    },
    createPool({ commit, state }, data) {
      const fd = new FormData();
      fd.append("name", data.name);
      fd.append("description", data.description);
      data.pool
        ? fd.append("pool", data.pool)
        : fd.append("tickers", JSON.stringify(data.tickers));
      return axios.post(`${url}/pool`, fd).then(res =>
        commit("setPools", [
          ...state.pools,
          {
            pool_id: res.data.pool_id,
            name: data.name,
            description: data.description,
            tickers: mapTickerIcons(res.data.tickers)
          }
        ])
      );
    },
    editPool({ commit, state }, data) {
      return axios.put(`${url}/pool/${data.id}`, data.poolData).then(res => {
        commit(
          "setPools",
          state.pools.map(pool => {
            if (pool.pool_id === data.id) {
              return {
                ...data.poolData,
                tickers: mapTickerIcons(res.data.tickers),
                pool_id: res.data.pool_id
              };
            } else {
              return pool;
            }
          })
        );
      });
    },
    getTickersWithIncompleteData({state, commit}, selectedDate) {
      return axios.get(`${url}/available_data/`).then(({data}) => {
        const tickersWithMissingData = state.selectedPool.tickers
          .map((t) => t.ticker)
          .filter((ticker) => moment(selectedDate).isBefore(moment(data[ticker].start)))
          .map((ticker) => ({
            ticker,
            ...data[ticker]
          }))
        commit("setTickersWithMissingData", tickersWithMissingData);
      });
    },
    deletePool({ commit, state }, id) {
      return axios.delete(`${url}/pool/${id}`).then(() =>
        commit(
          "setPools",
          state.pools.filter(pool => pool.pool_id !== id)
        )
      );
    },
    portfolioConversion({ state, commit }, data) {
      const portfolio =
        data && data.portfolio
          ? data.portfolio
          : state.portfolio.portfolio_input.custom_portfolio;
      const first = portfolio[0];
      const takeTableCash = data && data.portfolio;
      const valueType = state.isWholeStocks
        ? AMOUNT
        : takeTableCash && first.capital && state.cash
        ? CAPITAL
        : first.weight && state.cash
        ? WEIGHT
        : null;

      const conversionPortfolio = {};
      for (const [key, value] of Object.entries(
        reverseMapPortfolio(
          data.isConversingPointDetail ? state.pointDetail.values : portfolio
        )
      )) {
        conversionPortfolio[key] = prepareForConversion(value, valueType);
      }
      if (valueType === AMOUNT && conversionPortfolio[RESERVE_TICKER]) {
        conversionPortfolio[RESERVE_TICKER] = {
          capital: portfolio.find(obj => obj.ticker === RESERVE_TICKER).capital
        };
      }
      if (valueType !== null) {
        return axios
          .post(`${url}/portfolio/conversion`, {
            custom_portfolio: conversionPortfolio,
            start_cash: state.cash ? parseFloat(state.cash) : 0
          })
          .then(res => {
            const commitData = data.isConversingPointDetail
              ? {
                  ...state.pointDetail,
                  values: mapPortfolioToArray(res.data.custom_portfolio)
                }
              : mapPortfolioToArray(res.data.custom_portfolio);
            commit(
              data.isConversingPointDetail
                ? "setPointDetail"
                : "setCustomPortfolio",
              commitData
            );
          });
      } else {
        if (portfolio[0].weight && portfolio[0].capital && !state.cash) {
          commit(
            "setCustomPortfolio",
            portfolio.map(el => ({ weight: el.weight, ticker: el.ticker }))
          );
        }
        return Promise.resolve();
      }
    },
    checkTickers({ commit }, data) {
      commit("setMissingTickerFinished", false);
      let tickers_list = [];
      let last_index = data.length;
      if (last_index != 1) {
        data.forEach((ticker, index) => {
          let exist = true;
          return axios
            .post(`${url}/portfolio/checkTickers`, { ticker })
            .then(resp => {
              exist = resp.data;
              if (!exist) {
                tickers_list.push(ticker);
              }
              if (index == last_index - 1) {
                commit("setTickersNonExistList", tickers_list);
                commit("setMissingTickerFinished", true);
                if (tickers_list.length == 0) {
                  commit("setExistTickers", false);
                }
              }
            });
        });
      } else {
        commit("setExistTickers", false);
        let exist = true;
        let ticker = data[0];
        return axios
          .post(`${url}/portfolio/checkTickers`, { ticker })
          .then(resp => {
            exist = resp.data;
            if (!exist) {
              commit("setExistTickers", true);
              commit("setMissingTickerFinished", true);
            }
          });
      }
    },
    calculatePortfolioMetrics({ state, commit, dispatch }, payload) {
      if (!state.tickersNonExistList.length) {
        commit("setIsCalculatingMeasures", true);
        const data = {
          start: moment(state.dataSince).format("YYYY-MM-DD"),
          start_cash: state.capital ? state.capital : 0,
          freq: state.resamplingFrequency.value,
          global_limits: composeGlobalLimits(
            true,
            state.rules,
            state.selectedPool.pool_id
          ),
          custom_portfolio: JSON.stringify(
            reverseMapPortfolio(
              payload && payload.portfolio
                ? payload.portfolio
                : state.portfolio.portfolio_input.custom_portfolio
            )
          )
        };
        if (payload && payload.saveNewVersion)
          data.history_id = state.portfolio.history_id;

        const fd = new FormData();
        Object.entries(data).forEach(([key, value]) => fd.append(key, value));
        return axios
          .post(`${url}/portfolioMetrics/`, fd)
          .then(res => {
            if (payload && payload.portfolio) {
              commit("setCustomPortfolio", payload.portfolio);
            }
            if (
              (!data.history_id && !state.portfolio.history_id) ||
              data.history_id === state.portfolio.history_id
            ) {
              commit("setCustomPortfolioOutput", res.data.custom_portfolio);
              commit("setEachStockResults", res.data.each_stock_results);
            }
            if (payload && payload.saveNewVersion) {
              dispatch("getSavedPortfolios");
              dispatch("getPortfolioHistories", state.portfolio.portfolio_id);
            }
          })
          .finally(() => {
            if (
              !data.history_id ||
              data.history_id === state.portfolio.history_id
            ) {
              commit("setIsCalculatingMeasures", false);
            }
          });
      }
    }
  },
  getters: {
    availableTickers: state => state.availableTickers,
    duplicatesPrevented: state => state.duplicatesPrevented,
    isOptimizingPortfolio: state => state.isOptimizingPortfolio,
    pointDetail: state => state.pointDetail,
    portfolio: state => state.portfolio,
    recalculatedPortfolio: state => state.recalculatedPortfolio,
    removedTickers: state => state.removedTickers,
    rules: state => state.rules,
    dataSince: state => state.dataSince,
    specialPortfolioId: state => state.specialPortfolioId,
    wrongMinMaxPrevented: state => state.wrongMinMaxPrevented,
    wrongWeightSumPrevented: state => state.wrongWeightSumPrevented,
    expandedChart: state => state.expandedChart,
    replacePortfolioCoords: state => state.replacePortfolioCoords,
    savedPortfolios: state => state.savedPortfolios,
    portfoliosLoad: state => state.portfoliosLoad,
    newImportedPortfolios: state => state.newImportedPortfolios,
    notCalculatedPortfolios: state => state.notCalculatedPortfolios,
    cash: state => state.cash,
    useFractions: state => state.useFractions,
    isWholeStocks: state => state.isWholeStocks,
    resamplingFrequency: state => state.resamplingFrequency,
    getPoolSP100: state => state.poolSP100,
    pools: state => state.pools,
    poolToEdit: state => state.poolToEdit,
    selectedPool: state => state.selectedPool,
    saveModalItem: state => state.saveModalItem,
    editMode: state => state.editMode,
    tickersNonExistList: state => state.tickersNonExistList,
    missingTickerFinished: state => state.missingTickerFinished,
    existTickers: state => state.existTickers,
    isMissingTickerInPool: state => {
      return {
        isMissing: state.portfolio
          ? !state.portfolio.portfolio_input.custom_portfolio
              .map(item => item.ticker)
              .every(ticker =>
                state.selectedPool.tickers
                  .map(({ ticker }) => ticker)
                  .includes(ticker)
              )
          : false,
        missingTickers: state.portfolio
          ? state.portfolio.portfolio_input.custom_portfolio
              .map(item => item.ticker)
              .filter(
                ticker =>
                  !state.selectedPool.tickers
                    .map(({ ticker }) => ticker)
                    .includes(ticker)
              )
          : []
      };
    },
    portfolioHistories: state => state.portfolioHistories,
    selectedHistoryVersion: state => state.selectedHistoryVersion,
    isLoadingHistoryVersion: state => state.isLoadingHistoryVersion,
    defaultPortfolio: state => state.defaultPortfolio,
    isCalculatingFrontier: state => state.isCalculatingFrontier,
    isCalculatingSuggestion: state => state.isCalculatingSuggestion,
    suggestedOptimalization: state => state.suggestedOptimalization,
    isCalculatingMeasures: state => state.isCalculatingMeasures,
    tickersWithMissingData: state => state.tickersWithMissingData
  }
};
